import { type ComponentType } from 'react';
import { ArrowTrendingUpIcon, ArrowTrendingDownIcon } from '@heroicons/react/24/solid';
import { cn } from '@/utils/cn';
import { Skeleton } from './Skeleton';

export interface StatCardProps {
  title: string;
  value: string;
  subtitle?: string;
  icon?: ComponentType<{ className?: string }>;
  trend?: { value: number; isPositive: boolean };
  color?: string;
  isLoading?: boolean;
}

export function StatCard({ title, value, subtitle, icon: Icon, trend, isLoading }: StatCardProps) {
  if (isLoading) {
    return (
      <div className="rounded-xl border border-gray-200 bg-white p-5 dark:border-gray-700 dark:bg-gray-800">
        <Skeleton className="mb-3 h-4 w-24" />
        <Skeleton className="mb-2 h-8 w-32" />
        <Skeleton className="h-3 w-20" />
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-5 dark:border-gray-700 dark:bg-gray-800">
      <div className="mb-3 flex items-center justify-between">
        <span className="text-sm font-medium text-gray-500 dark:text-gray-400">{title}</span>
        {Icon && (
          <div className="rounded-lg bg-brand-50 p-2 dark:bg-brand-900/20">
            <Icon className="h-5 w-5 text-brand-600 dark:text-brand-400" />
          </div>
        )}
      </div>
      <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{value}</p>
      <div className="mt-1 flex items-center gap-1.5">
        {trend && (
          <span className={cn('flex items-center gap-0.5 text-xs font-medium', trend.isPositive ? 'text-green-600 dark:text-green-400' : 'text-red-500 dark:text-red-400')}>
            {trend.isPositive ? (
              <ArrowTrendingUpIcon className="h-3.5 w-3.5" />
            ) : (
              <ArrowTrendingDownIcon className="h-3.5 w-3.5" />
            )}
            {Math.abs(trend.value).toFixed(1)}%
          </span>
        )}
        {subtitle && <span className="text-xs text-gray-500 dark:text-gray-400">{subtitle}</span>}
      </div>
    </div>
  );
}
